export interface PickerEmoji { value: string; label: string; imageUrl?: string; group?: number; tags?: string[]; skins?: PickerEmoji[] }
interface EmojiData { unicode: string; hexcode: string; label: string; group?: number; tags?: string[]; skins?: EmojiData[] }
export const EMOJI_CATEGORIES = [
  { id: 0, label: "Smileys & emotion", icon: "😀" }, { id: 1, label: "People & body", icon: "👋" },
  { id: 3, label: "Animals & nature", icon: "🐻" }, { id: 4, label: "Food & drink", icon: "🍓" },
  { id: 5, label: "Travel & places", icon: "🚗" }, { id: 6, label: "Activities", icon: "⚽" },
  { id: 7, label: "Objects", icon: "💡" }, { id: 8, label: "Symbols", icon: "💕" }, { id: 9, label: "Flags", icon: "🏳️" },
];
// Use the canonical sequence, avoiding redundant VS16 that splits old reactions (e.g. 👍).
const mapEmoji = (entry: EmojiData): PickerEmoji => ({ value: String.fromCodePoint(...entry.hexcode.split("-").map(hex => parseInt(hex, 16))), label: entry.label,
  ...(entry.group !== undefined ? { group: entry.group === 2 ? 1 : entry.group } : {}),
  ...(entry.tags ? { tags: entry.tags } : {}), ...(entry.skins ? { skins: entry.skins.map(mapEmoji) } : {}),
});
let loaded: Promise<PickerEmoji[]> | undefined;
/** Bundled, self-hosted data, loaded only on picker open. No search API. */
export function loadEmojis(): Promise<PickerEmoji[]> {
  return loaded ??= import("emojibase-data/en/compact.json")
    .then(module => (module.default as EmojiData[]).filter(item => item.group !== undefined).map(mapEmoji))
    .catch(error => { loaded = undefined; throw error; });
}
export function emojiWithTone(emoji: PickerEmoji, tone: number): PickerEmoji {
  if (tone < 1 || tone > 5) return emoji;
  const modifier = String.fromCodePoint(0x1f3fa + tone);
  return emoji.skins?.find(skin => {
    const modifiers = [...skin.value].filter(char => /[\u{1F3FB}-\u{1F3FF}]/u.test(char));
    return modifiers.length > 0 && modifiers.every(char => char === modifier);
  }) ?? emoji;
}
export function filterEmojis(catalog: PickerEmoji[], custom: PickerEmoji[], query: string, group: number | "recent" | "custom", tone: number, recent: string[]): PickerEmoji[] {
  const normalized = query.toLowerCase().replace(/[_:]/g, " ").trim();
  const tokens = normalized.split(/\s+/).filter(Boolean);
  const all = [...custom, ...catalog];
  if (tokens.length) {
    return all.flatMap(emoji => [emoji, ...(emoji.skins ?? [])]).filter(emoji => {
      const text = [emoji.label, ...(emoji.tags ?? []), emoji.value].join(" ").toLowerCase();
      return tokens.every(token => text.includes(token));
    }).map(emoji => emoji.skins ? emojiWithTone(emoji, tone) : emoji)
      .filter((emoji, i, items) => items.findIndex(item => item.value === emoji.value) === i);
  }
  if (group === "recent") {
    const byValue = new Map(all.flatMap(emoji => [emoji, ...(emoji.skins ?? [])]).map(emoji => [emoji.value, emoji]));
    return recent.flatMap(value => byValue.has(value) ? [byValue.get(value)!] : []);
  }
  const items = group === "custom" ? custom : catalog.filter(emoji => emoji.group === group);
  return tone === -1 ? items.flatMap(emoji => [emoji, ...(emoji.skins ?? [])]) : items.map(emoji => emojiWithTone(emoji, tone));
}
export function rememberEmoji(recent: string[], value: string) { return [value, ...recent.filter(item => item !== value)].slice(0, 24); }
